import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useParams, useNavigate } from "react-router-dom";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import apiService from "../services/api";
import "./ProductDetails.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://printformee.com/api";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);
  const [categories, setCategories] = useState([]);

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  // Fetch categories for name resolution
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        const data = await response.json();
        setCategories(data.data || data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    if (!categories || !Array.isArray(categories)) return categoryId;
    const category = categories.find(
      (cat) => cat._id === categoryId || cat.id === categoryId,
    );
    return category ? category.name : categoryId;
  };

  const handleEditProduct = () => {
    // Create a lightweight version of product data without images to avoid quota exceeded error
    const lightweightProductData = {
      ...product,
      // Remove large image data to prevent localStorage quota issues
      thumbnail: product.thumbnail ? "EXISTS" : null,
      additional_images:
        product.additional_images?.length > 0 ? "EXISTS" : null,
      color_wise_images:
        product.color_wise_images &&
        typeof product.color_wise_images === "object" &&
        Object.keys(product.color_wise_images).length > 0
          ? "EXISTS"
          : null,
      meta_image: product.meta_image ? "EXISTS" : null,
    };

    // Store the lightweight product data for editing
    localStorage.setItem(
      "editProductData",
      JSON.stringify(lightweightProductData),
    );
    localStorage.setItem("editProductId", id);
    navigate(`/dashboard/add-new-product`);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        // Try to fetch from API first
        try {
          const productData = await apiService.getProduct(id);
          console.log("🔍 Product data loaded from API:", productData);
          console.log("🔍 Product data keys:", Object.keys(productData));
          console.log("🔍 Product name:", productData.name);
          console.log("🔍 Product description:", productData.description);
          console.log("🔍 Product brand:", productData.brand_id);
          console.log("🔍 Product category:", productData.category_id);

          // Process image URLs for gallery
          const images = [];

          // Handle main thumbnail
          if (productData.thumbnail) {
            // Cloudinary URLs start with https://res.cloudinary.com - use as-is
            // Local paths start with /uploads/ - prepend localhost
            // Full HTTP URLs start with http:// - use as-is
            const thumbnailUrl = productData.thumbnail.startsWith(
              "https://res.cloudinary.com",
            )
              ? productData.thumbnail
              : productData.thumbnail.startsWith("http://")
                ? productData.thumbnail
                : productData.thumbnail.startsWith("/uploads/")
                  ? `${API_BASE_URL}${productData.thumbnail}`
                  : `${API_BASE_URL}/uploads${productData.thumbnail}`;

            images.push({
              src: thumbnailUrl,
              alt: "Main Product Image",
            });
          }

          // Handle additional images
          if (Array.isArray(productData.additional_images)) {
            productData.additional_images.forEach((img, index) => {
              if (img) {
                // Cloudinary URLs start with https://res.cloudinary.com - use as-is
                // Local paths start with /uploads/ - prepend localhost
                // Full HTTP URLs start with http:// - use as-is
                const imageUrl = img.startsWith("https://res.cloudinary.com")
                  ? img
                  : img.startsWith("http://")
                    ? img
                    : img.startsWith("/uploads/")
                      ? `${API_BASE_URL}${img}`
                      : `${API_BASE_URL}/uploads${img}`;

                images.push({
                  src: imageUrl,
                  alt: `Additional Image ${index + 1}`,
                });
              }
            });
          }

          // Handle color-wise images
          if (
            productData.color_wise_images &&
            typeof productData.color_wise_images === "object"
          ) {
            Object.entries(productData.color_wise_images).forEach(
              ([color, img]) => {
                if (img) {
                  const imageUrl = img.startsWith("https://res.cloudinary.com")
                    ? img
                    : img.startsWith("http://")
                      ? img
                      : img.startsWith("/uploads/")
                        ? `${API_BASE_URL}${img}`
                        : `${API_BASE_URL}/uploads${img}`;

                  images.push({
                    src: imageUrl,
                    alt: `${color} Variant`,
                  });
                }
              },
            );
          }

          console.log("Gallery images prepared:", images.length, "images");
          console.log(
            "DEBUG - color_wise_images:",
            productData.color_wise_images,
          );
          console.log(
            "DEBUG - color_wise_images keys:",
            Object.keys(productData.color_wise_images || {}),
          );
          console.log(
            "DEBUG - color_wise_images values:",
            Object.values(productData.color_wise_images || {}),
          );
          setAllImages(images);
          setProduct(productData);
        } catch (apiError) {
          console.log(
            "API fetch failed, trying localStorage fallback:",
            apiError,
          );

          // Fallback to localStorage
          const stored = localStorage.getItem(id);
          if (stored) {
            const productData = JSON.parse(stored);
            console.log("Product data loaded from localStorage:", productData);

            // Prepare all images for gallery (base64 format)
            const images = [];
            if (
              productData.thumbnail &&
              productData.thumbnail.startsWith("data:")
            ) {
              images.push({
                src: productData.thumbnail,
                alt: "Main Product Image",
              });
            }
            if (Array.isArray(productData.additional_images)) {
              productData.additional_images.forEach((img, index) => {
                if (img && img.startsWith("data:")) {
                  images.push({
                    src: img,
                    alt: `Product Image ${index + 1}`,
                  });
                }
              });
            }

            console.log(
              "Gallery images prepared from localStorage:",
              images.length,
              "images",
            );
            setAllImages(images);
            setProduct(productData);
          } else {
            console.log("No product found in localStorage either");
          }
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Gallery functions
  const openLightbox = (imageIndex) => {
    setCurrentImageIndex(imageIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1,
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1,
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowLeft") goToPrevious();
    if (e.key === "ArrowRight") goToNext();
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (!product) return <Typography>Product not found</Typography>;

  return (
    <Box p={3}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIosIcon />}
          onClick={() => navigate("/dashboard/product-list")}
          sx={{
            textTransform: "none",
            color: "#1976d2",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          Back to Product List
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* LEFT IMAGE SECTION */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                component="img"
                src={
                  product.thumbnail
                    ? product.thumbnail.startsWith("data:")
                      ? product.thumbnail
                      : // Handle Cloudinary URLs (they start with https://res.cloudinary.com)
                        product.thumbnail.startsWith("https://")
                        ? product.thumbnail
                        : // Handle full HTTP URLs
                          product.thumbnail.startsWith("http://")
                          ? product.thumbnail
                          : // Handle relative paths
                            `${API_BASE_URL}${product.thumbnail}`
                    : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='16' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image Available%3C/text%3E%3C/svg%3E"
                }
                alt={product.name}
                onClick={() => product.thumbnail && openLightbox(0)}
                onError={(e) => {
                  console.log("Main image failed to load:", product.thumbnail);
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='16' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image Available%3C/text%3E%3C/svg%3E";
                }}
                onLoad={() => {
                  console.log("Main image loaded successfully");
                }}
                sx={{
                  width: "100%",
                  height: 400,
                  objectFit: "cover",
                  borderRadius: 1,
                  backgroundColor: "#f5f5f5",
                  cursor: product.thumbnail ? "pointer" : "default",
                  "&:hover": product.thumbnail
                    ? {
                        opacity: 0.9,
                        transform: "scale(1.02)",
                      }
                    : {},
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" mb={2}>
                All Product Images
              </Typography>

              <Grid container spacing={1}>
                {/* Thumbnail */}
                {product.thumbnail && (
                  <Grid item xs={4}>
                    <Box
                      component="img"
                      src={
                        product.thumbnail.startsWith("data:")
                          ? product.thumbnail
                          : product.thumbnail.startsWith("http")
                            ? product.thumbnail
                            : `${API_BASE_URL}${product.thumbnail}`
                      }
                      alt="Main Thumbnail"
                      onClick={() => openLightbox(0)}
                      onError={(e) => {
                        console.log(
                          "Thumbnail failed to load:",
                          product.thumbnail,
                        );
                        e.target.style.display = "none";
                      }}
                      onLoad={() => {
                        console.log("Thumbnail loaded successfully");
                      }}
                      sx={{
                        width: "100%",
                        aspectRatio: "1",
                        objectFit: "cover",
                        borderRadius: 1,
                        border: "2px solid #1976d2",
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.9,
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* Additional Images */}
                {Array.isArray(product.additional_images) &&
                  product.additional_images.length > 0 &&
                  product.additional_images.map((img, i) => (
                    <Grid item xs={4} key={`additional-${i}`}>
                      <Box
                        component="img"
                        src={
                          img
                            ? img.startsWith("data:")
                              ? img
                              : img.startsWith("http")
                                ? img
                                : `${API_BASE_URL}${img}`
                            : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='8' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E"
                        }
                        alt={`Additional Image ${i + 1}`}
                        onClick={() =>
                          img && openLightbox((product.thumbnail ? 1 : 0) + i)
                        }
                        onError={(e) => {
                          console.log(
                            `Additional image ${i} failed to load:`,
                            img,
                          );
                          e.target.style.display = "none";
                        }}
                        onLoad={() => {
                          console.log(
                            `Additional image ${i} loaded successfully`,
                          );
                        }}
                        sx={{
                          width: "100%",
                          aspectRatio: "1",
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                          cursor: img ? "pointer" : "default",
                          "&:hover": img
                            ? {
                                border: "2px solid #1976d2",
                                opacity: 0.9,
                                transform: "scale(1.05)",
                              }
                            : {},
                        }}
                      />
                    </Grid>
                  ))}

                {/* Color-wise Images */}
                {product.color_wise_images &&
                  typeof product.color_wise_images === "object" &&
                  Object.keys(product.color_wise_images).length > 0 &&
                  Object.entries(product.color_wise_images).map(
                    ([color, img], i) => (
                      <Grid item xs={4} key={`color-${i}`}>
                        <Box
                          component="img"
                          src={
                            img
                              ? img.startsWith("data:")
                                ? img
                                : img.startsWith("http")
                                  ? img
                                  : `${API_BASE_URL}${img}`
                              : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='6' fill='%23999' text-anchor='middle' dy='.3em'%3E${color}%3C/text%3E%3C/svg%3E"
                          }
                          alt={`${color} Variant`}
                          onClick={() =>
                            img &&
                            openLightbox(
                              (product.thumbnail ? 1 : 0) +
                                (product.additional_images?.length || 0) +
                                i,
                            )
                          }
                          onError={(e) => {
                            console.log(
                              `Color image ${color} failed to load:`,
                              img,
                            );
                            e.target.style.display = "none";
                          }}
                          onLoad={() => {
                            console.log(
                              `Color image ${color} loaded successfully`,
                            );
                          }}
                          sx={{
                            width: "100%",
                            aspectRatio: "1",
                            objectFit: "cover",
                            borderRadius: 1,
                            border: "1px solid #ddd",
                            cursor: img ? "pointer" : "default",
                            "&:hover": img
                              ? {
                                  border: "2px solid #1976d2",
                                  opacity: 0.9,
                                  transform: "scale(1.05)",
                                }
                              : {},
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: "center",
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          {color}
                        </Typography>
                      </Grid>
                    ),
                  )}

                {/* Show message if no images at all */}
                {!product.thumbnail &&
                  (!Array.isArray(product.additional_images) ||
                    product.additional_images.length === 0) &&
                  (!product.color_wise_images ||
                    Object.keys(product.color_wise_images).length === 0) && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        No images available for this product
                      </Typography>
                    </Grid>
                  )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT DETAILS SECTION */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              {product.name}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEditProduct}
                sx={{ bgcolor: "#1976d2", "&:hover": { bgcolor: "#1565c0" } }}
              >
                Edit Product
              </Button>
            </Box>
          </Box>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" color="primary" mb={1}>
                Description
              </Typography>
              {product.description || product.description_en ? (
                <div
                  className="product-description"
                  dangerouslySetInnerHTML={{
                    __html: product.description || product.description_en,
                  }}
                />
              ) : (
                <Typography variant="body2">No description available</Typography>
              )
            }
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    General Info
                  </Typography>
                  <Typography variant="body2">
                    <b>Brand:</b> {product.brand_id || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Category:</b> {getCategoryName(product.category_id)}
                  </Typography>
                  <Typography variant="body2">
                    <b>Sub Category:</b> {getCategoryName(product.subcategory) || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>SKU:</b> {product.sku}
                  </Typography>
                  <Typography variant="body2">
                    <b>Stock:</b> {product.stock}
                  </Typography>
                  <Typography variant="body2">
                    <b>Min Order Qty:</b> {product.min_order_qty || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Max Order Qty:</b>{" "}
                    {product.max_order_qty !== undefined &&
                    product.max_order_qty !== null
                      ? product.max_order_qty
                      : "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Weight:</b>{" "}
                    {product.weight !== undefined && product.weight !== null
                      ? product.weight
                      : "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Status:</b> {product.status}
                  </Typography>
                  <Typography variant="body2">
                    <b>Featured:</b> {product.is_featured ? "Yes" : "No"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    Price Info
                  </Typography>
                  <Typography variant="body2">
                    <b>Price:</b> ₹{product.price || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Discount Type:</b> {product.discount_type || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Discount:</b> ₹{product.discount_amount || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Tax:</b>{" "}
                    {product.tax_percent ? `${product.tax_percent}%` : "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Tax Calculation:</b> {product.tax_calculation || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Shipping Cost:</b> ₹{product.shipping_cost || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Shipping Multiply:</b>{" "}
                    {product.shipping_multiply_quantity ? "Yes" : "No"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Specifications - short scannable key/value pairs */}
            <Grid item xs={12}>
              <Card sx={{ mt: 1 }}>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    Specifications
                  </Typography>

                  {/* Build an ordered list of specification pairs */}
                  <Grid container spacing={1}>
                    {(() => {
                      const specs = [
                        { label: "Category", value: getCategoryName(product.category_id) || product.name || "N/A" },
                        { label: "Material", value: product.material || "Premium Ceramic" },
                        { label: "Capacity", value: product.capacity || "325 ml (11 oz)" },
                        { label: "Color", value: product.color || "Black" },
                        { label: "Finish", value: product.finish || "Glossy" },
                        { label: "Printing", value: product.printing || "HD Sublimation Printing" },
                        { label: "Magic Effect", value: product.magic_effect || "Heat Reveal" },
                        { label: "Microwave Safe", value: product.microwave_safe !== undefined ? (product.microwave_safe ? "Yes" : "No") : "No" },
                        { label: "Dishwasher Safe", value: product.dishwasher_safe !== undefined ? (product.dishwasher_safe ? "Yes" : "Hand Wash Recommended") : "Hand Wash Recommended" },
                        { label: "Customizable", value: product.customizable !== undefined ? (product.customizable ? "Yes (Photo Upload)" : "No") : "Yes (Photo Upload)" },
                        { label: "Print Area", value: product.print_area || "Full Wrap (360°)" },
                        { label: "Coating", value: product.coating || "Heat-Sensitive Color Changing" },
                        { label: "Weight", value: product.weight || "Approx. 350–400 g" },
                        { label: "Country of Origin", value: product.country_of_origin || "India" },
                      ];

                      // Render two-columns: label/value pairs placed as two pairs per row (left and right)
                      const rows = [];
                      for (let i = 0; i < specs.length; i += 2) {
                        const left = specs[i];
                        const right = specs[i + 1];
                        rows.push(
                          <Grid container spacing={1} key={`spec-row-${i}`} sx={{ mb: 0.5 }}>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="textSecondary">{left.label}</Typography>
                              <Typography variant="body2">{left.value}</Typography>
                            </Grid>

                            {right ? (
                              <Grid item xs={6} md={3}>
                                <Typography variant="body2" color="textSecondary">{right.label}</Typography>
                                <Typography variant="body2">{right.value}</Typography>
                              </Grid>
                            ) : (
                              <Grid item xs={6} md={3} />
                            )}

                            {/* Fill remaining space so rows align nicely */}
                            <Grid item xs={12} md={6} />
                          </Grid>,
                        );
                      }

                      return rows;
                    })()}

                    {/* Package Includes as a full-width row */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="textSecondary">Package Includes</Typography>
                        <Typography variant="body2">{product.package_includes || "1 × Personalized Magic Mug"}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Grid>
      </Grid>

      {/* SKU & VARIATIONS TABLE */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
            SKU & Variations
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>ID</b>
                </TableCell>
                <TableCell>
                  <b>SKU</b>
                </TableCell>
                <TableCell>
                  <b>Color</b>
                </TableCell>
                <TableCell>
                  <b>Size</b>
                </TableCell>
                <TableCell>
                  <b>Variation Wise Price</b>
                </TableCell>
                <TableCell>
                  <b>Stock</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(() => {
                const productVariations = Array.isArray(product?.variations)
                  ? product.variations
                  : Array.isArray(product?.variants)
                  ? product.variants
                  : [];

                console.log("FINAL VARIATIONS:", productVariations);

                if (productVariations.length > 0) {
                  return productVariations.map((v, i) => {
                    const color =
                      v.color ||
                      v.Color ||
                      v.attributes?.color ||
                      "-";

                    const size =
                      v.size ||
                      v.Size ||
                      v["mug size"] ||
                      v.attributes?.size ||
                      "-";

                    return (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          {v.sku || "-"}
                        </TableCell>
                        <TableCell>
                          {color}
                        </TableCell>
                        <TableCell>
                          {size}
                        </TableCell>
                        <TableCell>
                          ₹{v.price || v.variant_price || product.price || 0}
                        </TableCell>
                        <TableCell>
                          {v.stock || v.qty || product.stock || 0}
                        </TableCell>
                      </TableRow>
                    );
                  });
                }

                return (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No variations available
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* CUSTOMIZATION & MOCKUP */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
            Customization & Mockup
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Customization Status:</b>{' '}
            {product?.customization?.enabled ? 'Enabled' : (product?.customizable ? 'Enabled' : 'Disabled')}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Customization Type:</b>{' '}{product?.customization?.type || (product?.customization?.enabled ? 'Generic' : 'N/A')}
          </Typography>

          {/* Printable Areas */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" mb={1}>Printable Areas</Typography>

            {product?.customization?.printAreaMap && Object.keys(product.customization.printAreaMap).length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Area</b></TableCell>
                    <TableCell><b>X</b></TableCell>
                    <TableCell><b>Y</b></TableCell>
                    <TableCell><b>Width</b></TableCell>
                    <TableCell><b>Height</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(product.customization.printAreaMap).map(([area, rect]) => (
                    <TableRow key={area}>
                      <TableCell>{area}</TableCell>
                      <TableCell>{typeof rect.x === 'number' ? rect.x.toFixed(3) : rect.x}</TableCell>
                      <TableCell>{typeof rect.y === 'number' ? rect.y.toFixed(3) : rect.y}</TableCell>
                      <TableCell>{typeof rect.width === 'number' ? rect.width.toFixed(3) : rect.width}</TableCell>
                      <TableCell>{typeof rect.height === 'number' ? rect.height.toFixed(3) : rect.height}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">No printable areas saved</Typography>
            )}
          </Box>

          {/* Mockups */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" mb={1}>Mockups</Typography>

            {product?.customization?.mockups && Object.keys(product.customization.mockups).length > 0 ? (
              <Grid container spacing={1}>
                {Object.entries(product.customization.mockups).map(([position, url], i) => (
                  <Grid item xs={12} md={6} key={`mockup-${i}`}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Box
                        component="img"
                        src={
                          url?.startsWith('http') ? url : (url ? `${API_BASE_URL}${url}` : '')
                        }
                        alt={`Mockup ${position}`}
                        sx={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                        onError={(e) => { console.log('Mockup image failed to load:', url); e.target.style.display = 'none'; }}
                      />

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2"><b>{position}</b></Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {url}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {url ? (
                            <Button size="small" variant="outlined" onClick={() => window.open(url.startsWith('http') ? url : `${API_BASE_URL}${url}`, '_blank')}>Open</Button>
                          ) : (
                            <Typography variant="caption">No file uploaded</Typography>
                          )}

                          <Button size="small" variant="text" onClick={() => {
                            // copy url to clipboard for quick verification
                            if (url) navigator.clipboard && navigator.clipboard.writeText(url);
                          }}>Copy URL</Button>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">No mockup images uploaded</Typography>
            )}
          </Box>

          {/* Tools / Capabilities badges */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" mb={1}>Available Tools</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* If product.customization.tools is an array, mark enabled accordingly, otherwise show defaults if customizable */}
              {(['Images', 'Text', 'Shapes', 'Stickers', 'Uploads', 'Fonts', 'Colors']).map((tool) => {
                const enabled = Array.isArray(product?.customization?.tools)
                  ? product.customization.tools.includes(tool.toLowerCase())
                  : !!product?.customizable;
                return (
                  <Chip key={tool} label={tool} color={enabled ? 'success' : 'default'} variant={enabled ? 'filled' : 'outlined'} />
                );
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* SEO & META DATA */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
            Product SEO & Meta Data
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <b>Meta Title:</b> {product.meta_title || "N/A"}
              </Typography>
              <Typography variant="body2">
                <b>Meta Description:</b> {product.meta_description || "N/A"}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <b>Meta Image:</b>
                </Typography>
                {product.meta_image ? (
                  <Box
                    component="img"
                    src={
                      product.meta_image.startsWith(
                        "https://res.cloudinary.com",
                      )
                        ? product.meta_image
                        : product.meta_image.startsWith("http://")
                          ? product.meta_image
                          : product.meta_image.startsWith("/uploads/")
                            ? `${API_BASE_URL}${product.meta_image}`
                            : `${API_BASE_URL}/uploads${product.meta_image}`
                    }
                    alt="Meta Image"
                    onClick={() =>
                      window.open(
                        product.meta_image.startsWith(
                          "https://res.cloudinary.com",
                        )
                          ? product.meta_image
                          : product.meta_image.startsWith("http://")
                            ? product.meta_image
                            : product.meta_image.startsWith("/uploads/")
                              ? `${API_BASE_URL}${product.meta_image}`
                              : `${API_BASE_URL}/uploads${product.meta_image}`,
                        "_blank",
                      )
                    }
                    onError={(e) => {
                      console.log(
                        "Meta image failed to load:",
                        product.meta_image,
                      );
                      e.target.style.display = "none";
                    }}
                    onLoad={() => {
                      console.log("Meta image loaded successfully");
                    }}
                    sx={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: 2,
                      border: "1px solid #ddd",
                      cursor: "pointer",
                      "&:hover": {
                        border: "2px solid #1976d2",
                        opacity: 0.9,
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "120px",
                      height: "120px",
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      No Meta Image
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="body2">
                <b>Indexing Option:</b> {product.indexing_option || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <b>Max Snippet:</b> {product.max_snippet || "N/A"}
              </Typography>
              <Typography variant="body2">
                <b>Max Video Preview:</b> {product.max_video_preview || "N/A"}
              </Typography>
              <Typography variant="body2">
                <b>Max Image Preview:</b> {product.max_image_preview || "N/A"}
              </Typography>
              <Typography variant="body2">
                <b>Created At:</b>{" "}
                {product.created_at
                  ? new Date(product.created_at).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* PRODUCT VIDEO */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
            Product Video
          </Typography>

          {product.video_link ? (
            <Box>
              <Typography variant="body2">
                <b>Video URL:</b> {product.video_link}
              </Typography>
              <Box
                component="video"
                src={product.video_link}
                controls
                sx={{
                  width: "100%",
                  maxWidth: 400,
                  height: 225,
                  borderRadius: 1,
                  mt: 2,
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No product video available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* REVIEWS TABLE */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
            Product Reviews
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>SL</b>
                </TableCell>
                <TableCell>
                  <b>Review ID</b>
                </TableCell>
                <TableCell>
                  <b>Reviewer</b>
                </TableCell>
                <TableCell>
                  <b>Rating</b>
                </TableCell>
                <TableCell>
                  <b>Review</b>
                </TableCell>
                <TableCell>
                  <b>Reply</b>
                </TableCell>
                <TableCell>
                  <b>Date</b>
                </TableCell>
                <TableCell>
                  <b>Status</b>
                </TableCell>
                <TableCell>
                  <b>Action</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.isArray(product.reviews_list) &&
              product.reviews_list.length > 0 ? (
                product.reviews_list.map((review, index) => (
                  <TableRow key={review.id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{review.id}</TableCell>
                    <TableCell>{review.reviewer_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {"★".repeat(review.rating)}
                        {"☆".repeat(5 - review.rating)}
                      </Typography>
                    </TableCell>
                    <TableCell>{review.comment}</TableCell>
                    <TableCell>{review.reply || "No reply"}</TableCell>
                    <TableCell>{review.date}</TableCell>
                    <TableCell>
                      <Chip
                        label={review.status || "Pending"}
                        size="small"
                        color={
                          review.status === "Approved" ? "success" : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No reviews available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* IMAGE GALLERY LIGHTBOX */}
      <Modal
        open={lightboxOpen}
        onClose={closeLightbox}
        onKeyDown={handleKeyDown}
        aria-labelledby="image-gallery-modal"
        aria-describedby="image-gallery-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "900px",
            height: "90%",
            maxHeight: "700px",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">
              Image Gallery ({currentImageIndex + 1} / {allImages.length})
            </Typography>
            <IconButton onClick={closeLightbox}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Image Container */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              position: "relative",
              bgcolor: "#000",
            }}
          >
            {allImages[currentImageIndex] && (
              <Box
                component="img"
                src={allImages[currentImageIndex].src}
                alt={allImages[currentImageIndex].alt}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  width: "auto",
                  height: "auto",
                }}
                onLoad={() => {
                  console.log(
                    "Gallery image loaded:",
                    allImages[currentImageIndex].alt,
                  );
                }}
                onError={(e) => {
                  console.log(
                    "Gallery image failed to load:",
                    allImages[currentImageIndex].alt,
                  );
                }}
              />
            )}

            {/* Navigation Arrows */}
            {allImages.length > 1 && (
              <>
                <IconButton
                  onClick={goToPrevious}
                  sx={{
                    position: "absolute",
                    left: 10,
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "white",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <ArrowBackIosIcon />
                </IconButton>
                <IconButton
                  onClick={goToNext}
                  sx={{
                    position: "absolute",
                    right: 10,
                    bgcolor: "rgba(0,0,0,0.5)",
                    color: "white",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}
          </Box>

          {/* Footer with thumbnails */}
          {allImages.length > 1 && (
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
                maxHeight: "80px",
                overflowY: "auto",
              }}
            >
              {allImages.map((img, index) => (
                <Box
                  key={index}
                  component="img"
                  src={img.src}
                  alt={img.alt}
                  onClick={() => setCurrentImageIndex(index)}
                  sx={{
                    width: 50,
                    height: 50,
                    objectFit: "cover",
                    borderRadius: 1,
                    border:
                      currentImageIndex === index
                        ? "2px solid #1976d2"
                        : "1px solid #ddd",
                    cursor: "pointer",
                    opacity: currentImageIndex === index ? 1 : 0.6,
                    "&:hover": { opacity: 1 },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ProductDetails;
